import {Checkbox, Col, Form, Radio, Row} from "antd";
import DiagnosisTree from "../../../components/create/DiagnosisTree";
import ProceduresSelector from "../../../components/create/ProceduresSelector";
import TreatmentSelector from "../../../components/create/TreatmentSelector";
import HRGsSelector from "../../../components/create/HRGsSelector";
import TrustSelector from "../../../components/create/TrustSelector";
import SubICBLocationSelector from "../../../components/create/SubICBLocationSelector";
import GenderSelector from "../../../components/create/GenderSelector";
import AdmissionMethodSelector from "../../../components/create/AdmissionMethodSelector";
import AgeSelector from "../../../components/create/AgeSelector";
import CohortSelector from "../../../components/create/CohortSelector";
import React, {useEffect, useState} from "react";
import _ from "lodash";
import {isBlank, isNotBlank, isNotDefined} from "../../../reducers/util";
import {RadioChangeEvent} from "antd/lib/radio/interface";
import {useAppSelector} from "../../../hooks";
import {AdmissionMethod, CriterionHes} from "../../HealthcareUsageReport/types/CohortQuery";
import {fieldsWithErrors} from "../../../components/create/SelectUtil";

const EditCriterionHes = ({criterion, onChange, onFormReady}: { criterion: CriterionHes, onChange: (criteria: CriterionHes) => void, onFormReady: (ready: boolean) => void}) => {

    const validatingFields = ['diagnosis', 'diagnosisType', 'procedures', 'treatments', 'hrgs', 'trackedCohort']
    const icd10tree = useAppSelector((state) => state.settings.values.ICD10TREE)
    const procedureTree = useAppSelector((state) => state.settings.values.PROCEDURETREE)
    const [isOutpatient, setOutpatient] = useState(criterion.dataset === "outpatient")
    const [form] = Form.useForm();

    useEffect(() => {
        form.validateFields(['diagnosisType'])
    }, [form, isOutpatient])


    const datatypeChange = (e: RadioChangeEvent) => {
        setOutpatient(e.target.value === "outpatient")
        form
            .setFields([
                { name: 'diagnosis', value: [] },
                { name: 'diagnosisType', value: null }
            ]);
    }

    const hasAtLeastOneRequiredField = () => {
        const diag = form.getFieldValue('diagnosis')
        const diagnosisType = form.getFieldValue('diagnosisType')
        const procedures = form.getFieldValue('procedures')
        const treatments = form.getFieldValue('treatments')
        const hrgs = form.getFieldValue('hrgs')
        const trackedCohort = form.getFieldValue('trackedCohort')
        if (isBlank(diag) && isBlank(diagnosisType) && isBlank(procedures) && isBlank(treatments) && isBlank(hrgs) && isBlank(trackedCohort)) {
            return Promise.reject(new Error(`At least one of these is required: diagnosis or procedure or treatment specialty or hrg or a previously tracked cohort`));
        }
        return Promise.resolve();
    }

    const hasTrustOrSubICBLocationField = () => {
        const trusts = form.getFieldValue('trusts')
        const subICBLocations = form.getFieldValue('subICBLocations')
        if (isNotBlank(trusts) && isNotBlank(subICBLocations)) {
            return Promise.reject(new Error('Either Trust(s) or SUB ICB Location (Formerly CCG/HB)(s) can be chosen but not both.'));
        } else {
            return Promise.resolve();
        }
    }

    const hasDiagnonsisTypeWhenRequired = () => {
        const diagnosis = form.getFieldValue('diagnosis')
        const diagnosisType = form.getFieldValue('diagnosisType')
        const diagnosisIsDefined = !_.isNil(diagnosis) && diagnosis.length > 0
        const diagnosisTypeIsNotDefined = isNotDefined(diagnosisType) || diagnosisType.length === 0

        if (isOutpatient) {
            return Promise.resolve();
        }
        if (diagnosisIsDefined && diagnosisTypeIsNotDefined) {
            return Promise.reject(new Error('Choose at least one diagnosis type'));
        } if (!diagnosisIsDefined && !diagnosisTypeIsNotDefined) {
            return Promise.reject(new Error("Choose a diagnosis or remove diagnosis type"))
        } else {
            return Promise.resolve();
        }
    }

    const isFormValid = async (): Promise<boolean> => {
        const requiredFields = await hasAtLeastOneRequiredField()
            .then(() => true)
            .catch(() => false);
        return requiredFields && fieldsWithErrors(form).length === 0
    }

    const mutableCriterion = (JSON.parse(JSON.stringify(criterion)) as CriterionHes);

    if (mutableCriterion.dataset === undefined) {
        mutableCriterion.dataset = "inpatient";
    }
    if (mutableCriterion.admissionMethod === undefined) {
        mutableCriterion.admissionMethod = AdmissionMethod.ALL;
    }



    return <Form layout="vertical"
              form={form}
              initialValues={mutableCriterion}
              onFieldsChange={async (changedFields, allFields) => {
                  const valid = await isFormValid();
                  onFormReady(valid);
                  const values = allFields.map(field => {return {label: field.name.toString(), value: field.value}});
                  const mappedValues = values.reduce((prev: any, current) => {
                      prev[current.label] = current.value;
                      return prev;
                  }, {});
                  const updateCriterion = {
                      id: criterion!.id,
                      operator: criterion.operator,
                      ...mappedValues,
                  } as CriterionHes;
                  onChange(updateCriterion)
              }}
        >
            <Row>
        <Col span={16} data-testid="criteria-section-0">
            <Row className="sub-section">
                <Col span={24}>
                    <Form.Item
                        name="dataset"
                        label="Dataset"
                        dependencies={validatingFields}
                        rules={[
                            { required: true, message: "Choose at least one dataset" }
                        ]}
                    >
                        <Radio.Group style={{ width: '100%' }} onChange={datatypeChange}
                                     options={[
                                {label: "Inpatient", value: "inpatient"},
                                {label: "Outpatient", value: "outpatient"}
                            ]} onFocus={() => {
                                form.validateFields(['procedures'])
                        }} />
                    </Form.Item>
                </Col>
                <Col span={24}>
                    <div className="border-bottom">HES Fields</div>
                </Col>
                <Col span={24}>
                    <Row>
                        <Col span={14}>
                            <DiagnosisTree
                                form={form}
                                fieldId="diagnosis"
                                fieldName="Diagnosis"
                                fieldPlaceHolder="Select Diagnosis"
                                dependencies={validatingFields.concat(['diagnosisType'])}
                                customValidator={() => {
                                    return isOutpatient ? Promise.resolve() : hasAtLeastOneRequiredField()
                                }}
                                isDisabled={isOutpatient}
                                icd10tree={icd10tree ? icd10tree : []}
                                onChange={() => form.validateFields(["diagnosisType"])}
                            />
                        </Col>
                        <Col span={10}>
                            <Row>
                                <Col span={24}>
                                    <Form.Item
                                        name="diagnosisType"
                                        label="Diagnosis Type"
                                        dependencies={['diagnosis']}
                                        rules={[{ validator: hasDiagnonsisTypeWhenRequired }]}
                                    >
                                        <Checkbox.Group
                                            disabled={isOutpatient}
                                            style={{ width: '100%', paddingLeft: '5px' }}
                                        >
                                            <Checkbox value="primary">Primary</Checkbox>
                                            <span className="or">OR</span>
                                            <Checkbox value="secondary">Secondary</Checkbox>
                                        </Checkbox.Group>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Col>
                <Col span={24}>
                    <ProceduresSelector
                        form={form}
                        dependencies={validatingFields}
                        customValidator={hasAtLeastOneRequiredField}
                        procedureTree={procedureTree ? procedureTree : []}
                    />
                </Col>
                <Col span={24}>
                    <TreatmentSelector
                        dependencies={validatingFields}
                        fieldValidator={hasAtLeastOneRequiredField}
                        form={form}
                    />
                </Col>
                <Col span={24}>
                    <HRGsSelector
                        dependencies={validatingFields}
                        fieldValidator={hasAtLeastOneRequiredField}
                        form={form}
                    />
                </Col>
            </Row>
        </Col>
        <Col span={8}>
            <Row className="sub-section">
                <Col span={24}>
                    <Row>
                        <Col span={24}>
                            <TrustSelector
                                dependencies={["subICBLocations"]}
                                fieldValidator={hasTrustOrSubICBLocationField}
                                form={form}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24}>
                            <SubICBLocationSelector
                                dependencies={["trusts"]}
                                fieldValidator={hasTrustOrSubICBLocationField}
                                form={form}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                            <GenderSelector />
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={24} sm={24} md={18} lg={18} xl={18} xxl={18}>
                            <AdmissionMethodSelector isDisabled={isOutpatient} />
                        </Col>
                    </Row>
                    <Row>
                        <Col span={12}>
                            <AgeSelector form={form} />
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Row className="sub-section">
                <Col span={24}>
                    <CohortSelector
                        dependencies={validatingFields}
                        fieldValidator={hasAtLeastOneRequiredField}
                        form={form}
                    />
                </Col>
            </Row>
        </Col>
            </Row>
        </Form>
}

export default  EditCriterionHes;